import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { IAlert, IAlertButtonOptions } from "src/redux/state/Alert";
import { TextField, Grid, Button, useTheme } from "@mui/material";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { FlightChip } from "../Flights/FlightChip";
import { IFlight } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { openModal } from "src/redux/actions/App.Actions";
// form components
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { DialogModalStyles } from "./DialogModalWrapper";
/**
 * By Emily MacLeod July 2019
 * A modal component
 * data comes from store for RootModal (with the exception of 'fullscreen') or from props for EmbeddedModal
 *
 */
export interface IDialogModalProps {
  fullscreen?: boolean;
  data?: IAlert;
}

export const DialogModal = (props: IDialogModalProps): JSX.Element => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const rootData = useSelector((state: IRootState) => state.modal.root.data);
  const { data } = props;

  const [value, setValue] = useState<any>("");
  const [text, setText] = useState<any>(null);
  const [newDate, setNewDate] = useState<Date>(null);
  const [timeShift, setTimeShift] = useState<number>(null);
  const [enableAll, setEnableAll] = useState<boolean>(null);

  const handleClose = (): void => {
    setNewDate(null);
    setValue("");
    data.handleClose();
  };
  //if your array has an id & title, get the title out
  const getOption = (opt: any): any => {
    return data.formvaluekey ? opt[0] : opt;
  };

  //if you have an array with an id & title, get the id out
  const getValue = (opt: any): any => {
    return data.formvaluekey ? opt[1] : opt;
  };

  const handleonChange = (event: any): void => {
    setValue(event.target.value);
  };

  const handleClick = (): void => {
    switch (data.title) {
      case "Cancel Flights":
      case "Cancel Flight":
        data.handleSubmit(value, text);
        break;
      case "Config Aircraft":
        data.handleSubmit(value);
        break;
      case "Set New Start Time":
        data.handleSubmit(timeShift);
        setEnableAll(false);
        break;
      default:
        break;
    }
  };

  const handleTextField = (event: any): void => {
    setText(event.target.value);
  };

  const handleTimeChange = (): void => {
    const shift = moment(newDate).diff(moment(data.date), "minutes", true);

    if (isNaN(shift)) {
      //no change to time input
      setEnableAll(false);
    } else {
      setTimeShift(shift);
      const direction = shift > 0 ? "forward" : "back";
      const time = Math.abs(shift);
      //change timeshift format...
      const days = Math.abs(Math.trunc(moment.duration(time, "minutes").as("days")));
      const hours = Math.abs(Math.trunc(moment.duration(time - days * 24 * 60, "minutes").as("hours")));
      const minutes = Math.abs(
        Math.trunc(moment.duration(time - days * 24 * 60 - hours * 60, "minutes").as("minutes"))
      );
      const createMessage = (): string => {
        const format = (timeValue: number, timeUnit: string): string => {
          if (timeValue === 0) {
            return "";
          }
          switch (timeUnit) {
            case "minute":
              return timeValue > 1 ? `${timeValue} minutes` : `${timeValue} minute`;
            case "hour":
              if (minutes === 0) {
                return timeValue > 1 ? `${timeValue} hours` : `${timeValue} hour`;
              } else {
                return timeValue > 1 ? `${timeValue} hours and` : `${timeValue} hour and`;
              }
            case "day":
              if (hours === 0 && minutes === 0) {
                return timeValue > 1 ? `${timeValue} days` : `${timeValue} day`;
              } else {
                return timeValue > 1 ? `${timeValue} days and` : `${timeValue} day and`;
              }
            default:
              return "";
          }
        };
        return time === 0
          ? "Selection will not shift flight group"
          : `Selection will shift flight group ${direction} by ${format(days, "day")} ${format(hours, "hour")} ${format(
              minutes,
              "minute"
            )}`;
      };
      const message = { message: createMessage() };
      const summary = { ...data, ...message };
      dispatch(
        openModal({
          timeline: {
            data: summary,
          },
        })
      );
      setEnableAll(true);
    }
    if (shift === 0) {
      //user sets time back to original
      setEnableAll(false);
    }
  };

  const handleDateChange = (value: typeof newDate): void => {
    setNewDate(value);
    //data.handleChange(newDate)
  };

  const checkButton = (disabled: boolean): boolean => {
    if (enableAll) {
      return false;
    } else if (disabled) {
      return true;
    } else {
      return false;
    }
  };

  ////////////////////
  // render methods //
  ////////////////////

  const renderTitle = (data: IAlert): JSX.Element => {
    return (
      <DialogTitle id="alert-dialog-title" className={"title"}>
        {data.title}
      </DialogTitle>
    );
  };

  const renderMessage = (data: IAlert): JSX.Element => {
    return (
      <DialogContentText id="alert-dialog-description" className={"message"}>
        {data.message}
      </DialogContentText>
    );
  };

  const renderTextField = (data: IAlert): JSX.Element => {
    return (
      <DialogContent>
        <TextField id={data.textField} label={data.textFieldLabel} onChange={handleTextField} />
      </DialogContent>
    );
  };

  const renderButton = (data: IAlert): JSX.Element => {
    return (
      <DialogActions>
        {data.buttons.map((opt: IAlertButtonOptions) => (
          <Button
            id={opt.label + "_btn"}
            key={opt.label}
            disabled={checkButton(opt.disabled)}
            onClick={opt.handler || handleClick}
            color="primary"
            autoFocus={true}
          >
            {opt.label}
          </Button>
        ))}
      </DialogActions>
    );
  };

  const renderNativeForm = (data: IAlert): JSX.Element => {
    return (
      <DialogContent>
        <form className={"nativeForm"} noValidate autoComplete="off">
          <TextField
            id="standard-select-native"
            select
            className="test"
            value={value}
            onChange={handleonChange}
            SelectProps={{
              native: true,
              MenuProps: {
                className: "menu",
              },
            }}
            margin="dense"
            fullWidth={true}
          >
            <option key="0" value="0">
              {data.placeholder}
            </option>
            {data.formvalue.map((option: any) => (
              <option key={getValue(option)} value={getValue(option)}>
                {getOption(option)}
              </option>
            ))}
          </TextField>
        </form>
      </DialogContent>
    );
  };

  const renderFlightChips = (data: IAlert): JSX.Element => {
    return (
      <DialogContent>
        <Grid container direction={"row"} justifyContent={"center"}>
          {data.selectedFlights.map((selectedFlight: IFlight, index: number) => (
            <FlightChip data={selectedFlight} key={index} />
          ))}
        </Grid>
      </DialogContent>
    );
  };

  const renderDateTimeInput = (data: IAlert): JSX.Element => {
    return (
      <DialogContent>
        <DateTimeField
          className={"datetimeInput"}
          name="dialog_modal_date"
          label="From:"
          value={newDate?.toISOString ? newDate?.toISOString() : moment(data.date).toISOString()}
          onChange={(value) => handleDateChange(new Date(value))}
          onBlur={handleTimeChange}
        />
      </DialogContent>
    );
  };

  ////////////
  // return //
  ////////////

  if (!props.fullscreen && !!data) {
    //EMBEDDED MODE
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          {!!data.selectedFlights && renderFlightChips(data)}
          {!!data.title && renderTitle(data)}
          {!!data.message && renderMessage(data)}
          {!!data.date && renderDateTimeInput(data)}
          {!!data.formvalue && renderNativeForm(data)}
          {!!data.textField && renderTextField(data)}
          {!!data.buttons && renderButton(data)}
        </div>
      </DialogContent>
    );
  } else if (props.fullscreen === true && !!rootData) {
    // FULLSCREEN MODE
    return (
      <Dialog
        fullScreen={false}
        sx={DialogModalStyles(theme)}
        className={"fullscreen"}
        open={rootData !== null}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {!!rootData.title && renderTitle(rootData)}
        {!!rootData.message && renderMessage(rootData)}
        {!!rootData.date && renderDateTimeInput(rootData)}
        {!!rootData.formvalue && renderNativeForm(rootData)}
        {!!rootData.textField && renderTextField(rootData)}
        {!!rootData.buttons && renderButton(rootData)}
      </Dialog>
    );
  }

  return null;
};
